<script setup>
import { ref, computed, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useEmployerStore } from '../../../stores/EmployerStore'
import { RemindersCalendarService } from '../../../services/RemindersCalendarService'
import EmailInput from '../../Reminders/EmailInput.vue'

const employerStore = useEmployerStore()
const { userData } = storeToRefs(employerStore)
const { getEmails } = RemindersCalendarService()

const props = defineProps({
  shareUpdateData: { type: Object, required: true }
})

// Extract data from shareUpdateData
const employerName = computed(() => props.shareUpdateData.employerName || '')
const requirementTitle = computed(() => props.shareUpdateData.requirementTitle || '')
const riskScoreBefore = computed(() => props.shareUpdateData.riskScoreBefore || '--')
const riskScoreAfter = computed(() => props.shareUpdateData.riskScoreAfter || '--')
const riskAmountBefore = computed(() => props.shareUpdateData.riskAmountBefore || '--')
const riskAmountAfter = computed(() => props.shareUpdateData.riskAmountAfter || '--')
const fromEmail = computed(() => props.shareUpdateData.fromEmail || 'notifications@bcspro.com')
const contactEmails = computed(() => props.shareUpdateData.contactEmails || [])

const emit = defineEmits(['back', 'send-email'])

// Notes state
const MAX_CHARACTERS = 500
const notes = ref('')
const notesApplied = ref(false)

const charactersRemaining = computed(() => MAX_CHARACTERS - notes.value.length)

function clearNotes() {
  notes.value = ''
  notesApplied.value = false
}

function applyNotesToEmail() {
  notesApplied.value = true
}

// Agency users for checkboxes
const agencyUsers = ref([])
const isLoadingAgencyUsers = ref(false)

// Recipients state - agency users selected via checkboxes
const selectedRecipients = ref(new Set())
// Additional emails - initialize from contactEmails (employer contacts)
const additionalEmails = ref([...contactEmails.value])
const newEmailInput = ref('')

// Load agency users when userData is available
watch(userData, async (data) => {
  const agencyId = data?.agencyId
  if (!agencyId) return
  
  isLoadingAgencyUsers.value = true
  const emails = await getEmails(agencyId)
  agencyUsers.value = emails?.map(e => e.email) || []
  isLoadingAgencyUsers.value = false
}, { immediate: true })

function toggleRecipient(email) {
  const newSet = new Set(selectedRecipients.value)
  if (newSet.has(email)) {
    newSet.delete(email)
  } else {
    newSet.add(email)
  }
  selectedRecipients.value = newSet
}

function isRecipientSelected(email) {
  return selectedRecipients.value.has(email)
}

// Computed: all selected emails for "To" field display
const allSelectedEmails = computed(() => {
  const emails = [...selectedRecipients.value, ...additionalEmails.value]
  return emails.join(', ')
})

// Computed: formatted date for email preview
const formattedDate = computed(() => {
  const now = new Date()
  const options = { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true }
  return now.toLocaleString('en-US', options).replace(',', ' at')
})

// Computed: agency name from logged in user
const companyName = computed(() => {
  return userData.value?.companyName || ''
})

// Computed: logged in user full name
const userFullName = computed(() => {
  return userData.value?.fullname || ''
})

// Handle actions
function handleCancel() {
  emit('back')
}

function handleSendEmail() {
  const payload = {
    recipients: [...selectedRecipients.value, ...additionalEmails.value],
    notes: notesApplied.value ? notes.value : '',
    companyName: companyName.value,
    userFullName: userFullName.value,
    employerName: employerName.value,
    requirementTitle: requirementTitle.value,
    riskScoreBefore: riskScoreBefore.value,
    riskScoreAfter: riskScoreAfter.value,
    riskAmountBefore: riskAmountBefore.value,
    riskAmountAfter: riskAmountAfter.value,
    formattedDate: formattedDate.value
  }
  emit('send-email', payload)
}

// Check if can send (at least one recipient)
const canSend = computed(() => {
  return selectedRecipients.value.size > 0 || additionalEmails.value.length > 0
})
</script>

<template>
  <div class="share-update-container">
    <!-- Back button -->
    <div class="mb-3 mt-4.5">
      <button 
        id="backToActionCenterButton" 
        type="button" 
        class="btn btn-link text-decoration-none px-0 text-primary text-14" 
        @click="handleCancel"
      >
        <i class="bi bi-chevron-left"></i><strong> Back to Main Action Center</strong>
      </button>
    </div>

    <!-- Title -->
    <h2 id="shareUpdateTitle" class="text-28 mb-2">
      <med>Share {{ employerName }} Update via Email</med>
    </h2>
    <p id="shareUpdateDescription" class="text-14 text-muted mb-4 pb-3">
      You can share this update via email, along with personalized notes, to your chosen recipients.
    </p>

    <!-- Main Card -->
    <div id="shareUpdateCard" class="card border">
      <!-- Card Header -->
      <div class="card-header bg-light border-bottom px-3.5 py-3">
        <p class="header-20 mb-0"><strong>Preview, Customize, and Choose Email Recipients</strong></p>
      </div>

      <!-- Card Body -->
      <div class="card-body p-0 pt-1.5">
        <div class="row g-0">
          <!-- Left Panel: Email Preview (scrollable) -->
          <div id="emailPreviewPanel" class="col-md-5 border-end p-4 email-preview-panel">
            <h6 class="text-16 mb-3.5"><strong>Email Preview:</strong></h6>
            
            <div class="email-preview-content">
              <!-- Email metadata -->
              <div id="emailMetadata" class="mb-3.5 text-14">
                <div class="row mb-1">
                  <div class="col-auto text-muted" style="width: 80px;">From:</div>
                  <div class="col">{{ companyName }} &lt;{{ fromEmail }}&gt;</div>
                </div>
                <div class="row mb-1">
                  <div class="col-auto text-muted" style="width: 80px;">To:</div>
                  <div class="col text-truncate">{{ allSelectedEmails || '--' }}</div>
                </div>
                <div class="row mb-1">
                  <div class="col-auto text-muted" style="width: 80px;">Subject:</div>
                  <div class="col">{{ employerName }} - Risk Score Update</div>
                </div>
                <div class="row">
                  <div class="col-auto text-muted" style="width: 80px;">Date:</div>
                  <div class="col">{{ formattedDate }}</div>
                </div>
              </div>

              <!-- Email body preview -->
              <div class="email-body text-16">
                <p class="mb-3.5">Hi there,</p>
                
                <p class="mb-3">
                  Good news! {{ companyName }} has updated the {{ employerName }} 
                  risk score and dollar amount by marking a requirement complete.
                </p>

                <div class="mb-2.5">
                  <p class="mb-1">Completed Requirement:</p>
                  <p class="fw-bold mb-0">{{ requirementTitle }}</p>
                </div>

                <div class="mb-2.5">
                  <p class="mb-1.5">Improved Risk Score:</p>
                  <p class="mb-0">
                    <strong>{{ riskScoreAfter }}</strong>
                    <span> (formerly {{ riskScoreBefore }})</span>
                  </p>
                </div>

                <div class="mb-2.5">
                  <p class="mb-0.5">Decreased Risk Dollar Amount:</p>
                  <p class="mb-0">
                    <strong>{{ riskAmountAfter }}</strong>
                    <span> (formerly {{ riskAmountBefore }})</span>
                  </p>
                </div>

                <!-- Notes preview - always show, with placeholder if not applied -->
                <div class="mb-3.5">
                  <p class="mb-1">Notes from {{ userFullName }} at {{ companyName }}:</p>
                  <p v-if="notesApplied && notes" class="fw-bold mb-0">{{ notes }}</p>
                  <p v-else class="fw-bold mb-0">Your notes to {{ employerName }} recipients will appear here.</p>
                </div>

                <p class="mb-3">
                  Thank you for helping us stay on top of these compliance 
                  requirements. If you have any questions, please don't 
                  hesitate to reach out.
                </p>
              </div>
            </div>
          </div>

          <!-- Right Panel: Notes and Recipients -->
          <div id="notesAndRecipientsPanel" class="col-md-7">
            <!-- Add Notes Section -->
            <div class="notes-section p-4">
              <h6 class="text-16 mb-3.5">
                <strong>Add Notes to {{ employerName }} Recipients (Optional):</strong>
              </h6>
              <p class="text-14 mb-2">
                These notes will appear in the body of the email.
              </p>
              
              <textarea
                id="shareUpdateNotesTextarea"
                class="form-control text-16 mb-0 pb-0 optional-notes pt-2"
                rows="4"
                v-model="notes"
                :maxlength="MAX_CHARACTERS"
                placeholder="This is an example note filled out by the user regarding the email share."
              ></textarea>
              
              <div class="d-flex flex-column align-items-end pb-2">
                <div>
                  <span class="text-12 text-muted">{{ charactersRemaining }} characters remaining</span>
                </div>
                <div class="d-flex gap-3 mt-2">
                  <button
                    id="clearNotesButton"
                    type="button"
                    class="btn btn-link text-primary text-decoration-none p-0 text-14"
                    @click="clearNotes"
                    :disabled="!notes"
                  >
                  <med>
                    Clear Notes
                  </med>
                  </button>
                  <button
                    id="applyNotesButton"
                    type="button"
                    class="btn btn-primary btn-sm text-14 px-3"
                    @click="applyNotesToEmail"
                    :disabled="!notes || notesApplied"
                  >
                  <med>
                    Apply Notes to Email
                  </med>
                  </button>
                </div>
              </div>
            </div>

            <!-- Choose Recipients Section -->
            <div class="recipients-section p-4 mt-1">
              <h6 class="text-16 mb-3.5">
                <strong>Choose Recipient(s) for This Update:</strong>
              </h6>
              
              <!-- Agency user email checkboxes -->
              <div id="recipientCheckboxes" class="recipients-list mb-3.5">
                <div v-if="isLoadingAgencyUsers" class="text-muted text-14">
                  Loading users...
                </div>
                <template v-else>
                  <div 
                    v-for="email in agencyUsers" 
                    :key="email" 
                    class="form-check mb-1.5"
                  >
                    <input 
                      class="form-check-input" 
                      type="checkbox" 
                      :id="`recipient-${email}`"
                      :checked="isRecipientSelected(email)"
                      @change="toggleRecipient(email)"
                    />
                    <label 
                      class="form-check-label text-16" 
                      :class="{ 'text-primary fw-bold': isRecipientSelected(email) }"
                      :for="`recipient-${email}`"
                    >
                      {{ email }}
                    </label>
                  </div>
                  <div v-if="agencyUsers.length === 0" class="text-muted text-14">
                    No agency users available.
                  </div>
                </template>
              </div>

              <!-- Add Additional Recipients -->
              <div class="add-recipients-section">
                <p class="text-16 mb-1"><strong>Add Additional Recipient(s)</strong></p>
                <p class="text-14 text-muted mb-2">
                  Type an email address and press enter or separate with commas. Enter as many emails as needed.
                </p>

                <EmailInput 
                  id="additionalRecipientsInput"
                  v-model="additionalEmails" 
                  v-model:newEmail="newEmailInput"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Footer buttons -->
    <div class="d-flex justify-content-end gap-3 mt-4.5">
      <button
        id="cancelEmailButton"
        type="button"
        class="btn btn-outline-primary text-16 btn-lg header-20 px-3"
        @click="handleCancel"
      >
        <med> Cancel Email </med>
      </button>
      <button
        id="sendEmailButton"
        type="button"
        class="btn btn-primary text-16 header-20 px-3"
        :disabled="!canSend"
        @click="handleSendEmail"
      >
        <med> Send Email </med>
        <i class="bi bi-send-fill ms-1"></i>
      </button>
    </div>
  </div>
</template>

<style scoped lang="scss">
.share-update-container {
  padding-left: 9.5%;
  padding-right: 8.2%;
  padding-bottom: 2rem;
  min-height: 68vh;
}

.email-preview-panel {
  overflow-y: auto;
}

.optional-notes{
  height: 115px;
}

.notes-section {
  border-bottom: 1px solid var(--bs-gray-300);
}
</style>
